// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { useLogout } from '@/authentication/hooks/use-logout';
import { EnvironmentMenu } from '@/components/environment-menu';
import {
  AccountCircleOutlined,
  ArrowDropDownRounded,
  ArrowRightRounded,
  CodeOutlined,
  ContactSupportOutlined,
  CorporateFareRounded,
  ExtensionOutlined,
  Launch,
  LogoutRounded,
  MenuBookOutlined,
  MonitorOutlined,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { MouseEvent, useRef, useState } from 'react';

/**
 * A dropdown menu for user account actions, administration, and help resources.
 * Includes user profile information, settings, environment switching, and sign out.
 */
export function UserDropdownMenu() {
  const menu = useDisclosure();
  const environmentMenu = useDisclosure();
  const logout = useLogout();
  const accountMenuAnchor = useRef<HTMLButtonElement>(null);
  const [environmentAnchorEl, setEnvironmentAnchorEl] =
    useState<null | HTMLElement>(null);

  const { user } = useCurrentUser();
  const env = import.meta.env.VITE_ENVIRONMENT_NAME;

  return (
    <>
      <IconButton
        ref={accountMenuAnchor}
        onClick={menu.open}
        sx={{
          padding: 0.5,
          border: '2px solid',
          borderColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
        }}
      >
        <Avatar sx={{ width: 32, height: 32 }}>
          {user.firstName.at(0)}
          {user.lastName.at(0)}
        </Avatar>
        <ArrowDropDownRounded fontSize="small" />
      </IconButton>
      <Menu
        open={menu.isOpen}
        anchorEl={accountMenuAnchor.current}
        onClose={menu.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: 240,
            marginTop: 1,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          paddingX={2}
          paddingY={1}
        >
          <Avatar sx={{ width: 32, height: 32 }}>
            {user.firstName.at(0)}
            {user.lastName.at(0)}
          </Avatar>
          <Stack>
            <Typography variant="body2" fontWeight={600}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <ListSubheader sx={{ lineHeight: 3 }}>Administration</ListSubheader>
        <MenuItem
          component={Link}
          to="/account"
          onClick={menu.close}
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
        >
          <ListItemIcon>
            <AccountCircleOutlined fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">User account</Typography>
        </MenuItem>
        <MenuItem
          component={Link}
          to="/notifications/subscriptions"
          onClick={menu.close}
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
        >
          <ListItemIcon>
            <SettingsOutlined fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Notification subscriptions</Typography>
        </MenuItem>
        <MenuItem
          component={Link}
          to="/organization"
          onClick={menu.close}
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
        >
          <ListItemIcon>
            <CorporateFareRounded fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Organization settings</Typography>
        </MenuItem>
        <MenuItem
          component={Link}
          to="/integrations"
          onClick={menu.close}
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
        >
          <ListItemIcon>
            <ExtensionOutlined fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Integrations</Typography>
        </MenuItem>
        <MenuItem
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
          onClick={(e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            setEnvironmentAnchorEl(e.currentTarget);
            environmentMenu.open();
          }}
        >
          <ListItemIcon>
            <MonitorOutlined fontSize="small" />
          </ListItemIcon>
          <Stack direction="row" alignItems="center" width="100%">
            <Typography variant="body2">Environment</Typography>
            <ArrowRightRounded sx={{ marginLeft: 'auto' }} />
          </Stack>
          <Menu
            anchorEl={environmentAnchorEl}
            anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
            transformOrigin={{ vertical: 'center', horizontal: 'right' }}
            sx={{ marginLeft: -1 }}
            open={environmentMenu.isOpen}
            onClose={() => {
              setEnvironmentAnchorEl(null);
              environmentMenu.close();
            }}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              setEnvironmentAnchorEl(null);
              environmentMenu.close();
              menu.close();
            }}
          >
            <EnvironmentMenu
              currentEnvironment={env === 'sandbox' ? 'sandbox' : 'prod'}
            />
          </Menu>
        </MenuItem>
        <Divider />
        <ListSubheader sx={{ lineHeight: 3 }}>Help & docs</ListSubheader>
        <MenuItem
          component="a"
          href="https://guide.hardfin.com/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={menu.close}
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
        >
          <ListItemIcon>
            <MenuBookOutlined fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">User guide</Typography>
          <Launch color="disabled" sx={{ marginLeft: 'auto', fontSize: 16 }} />
        </MenuItem>
        <MenuItem
          component="a"
          href="https://docs.hardfin.com/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={menu.close}
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
        >
          <ListItemIcon>
            <CodeOutlined fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">API docs</Typography>
          <Launch color="disabled" sx={{ marginLeft: 'auto', fontSize: 16 }} />
        </MenuItem>
        <MenuItem
          component="a"
          href="mailto:support@hardfin.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={menu.close}
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
        >
          <ListItemIcon>
            <ContactSupportOutlined fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Contact support</Typography>
          <Launch color="disabled" sx={{ marginLeft: 'auto', fontSize: 16 }} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            menu.close();
            logout();
          }}
          sx={{ paddingX: 1, marginX: 1, borderRadius: 1 }}
        >
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Sign out</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
